import { useCallback, useEffect, useRef, useState } from "react";
import { colRefs, onSnapshot } from "../services/firestore";
import {
  mutateSecurePlanning,
  mutateSecurePlanningProgress,
} from "../services/secureBusinessWrites";
import { SNIJ_GROEPEN } from "../constants";

const makeEmptyGroupMap = () =>
  SNIJ_GROEPEN.reduce((result, group) => {
    result[group] = [];
    return result;
  }, {});

const normalizeGroupMap = (raw = {}, mapItem = (item) => item) =>
  SNIJ_GROEPEN.reduce((result, group) => {
    result[group] = Array.isArray(raw[group])
      ? raw[group]
          .filter(
            (item) =>
              item &&
              typeof item === "object" &&
              !Array.isArray(item)
          )
          .map(mapItem)
      : [];
    return result;
  }, {});

const rowKeyFor = (entry) =>
  `${String(entry?.koelcel || "")}-${Number(entry?.rij || 0)}`;

const createPlanningEntryId = (snijgroep, entry) => {
  const randomPart =
    window.crypto?.randomUUID?.() ||
    Math.random().toString(36).slice(2);
  return [
    snijgroep,
    entry?.koelcel,
    entry?.rij,
    Date.now().toString(36),
    randomPart,
  ]
    .map(String)
    .join("-")
    .slice(0, 160);
};

export default function usePlanningRijen({ enabled }) {
  const [queues, setQueues] = useState(makeEmptyGroupMap);
  const [completedEntryIdsByGroup, setCompletedEntryIdsByGroup] =
    useState(makeEmptyGroupMap);
  const [loading, setLoading] = useState(false);
  const queuesRef = useRef(queues);
  const pendingAddsRef = useRef(new Map());

  const updateQueues = useCallback((nextValue) => {
    const nextQueues =
      typeof nextValue === "function"
        ? nextValue(queuesRef.current)
        : nextValue;
    queuesRef.current = nextQueues;
    setQueues(nextQueues);
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;

    setLoading(true);
    return onSnapshot(
      colRefs.planningDoc(),
      (snapshot) => {
        const serverQueues = normalizeGroupMap(
          snapshot.exists() ? snapshot.data()?.queues : {},
          (entry) => ({ ...entry })
        );
        pendingAddsRef.current.forEach(({ snijgroep, entry }) => {
          if (
            !serverQueues[snijgroep].some(
              (candidate) => String(candidate.id) === String(entry.id)
            )
          ) {
            serverQueues[snijgroep].push(entry);
          }
        });
        updateQueues(serverQueues);
        setLoading(false);
      },
      () => setLoading(false)
    );
  }, [enabled, updateQueues]);

  useEffect(() => {
    if (!enabled) return undefined;

    return onSnapshot(colRefs.planningProgressDoc(), (snapshot) => {
      setCompletedEntryIdsByGroup(
        normalizeGroupMap(
          snapshot.exists()
            ? snapshot.data()?.completedEntryIdsByGroup
            : {},
          String
        )
      );
    });
  }, [enabled]);

  const addEntry = useCallback(
    async (snijgroep, entry, options = {}) => {
      if (!SNIJ_GROEPEN.includes(snijgroep)) {
        return { ok: false, reason: "invalid-group" };
      }
      const optimisticEntry = {
        ...entry,
        id: createPlanningEntryId(snijgroep, entry),
        snijgroep,
        allowMixedParties: options.allowMixedParties === true,
      };
      pendingAddsRef.current.set(optimisticEntry.id, {
        snijgroep,
        entry: optimisticEntry,
      });
      updateQueues((currentQueues) => ({
        ...currentQueues,
        [snijgroep]: [
          ...(currentQueues[snijgroep] || []).filter(
            (candidate) =>
              String(candidate.id) !== String(optimisticEntry.id)
          ),
          optimisticEntry,
        ],
      }));

      try {
        const result = await mutateSecurePlanning({
          operation: "add",
          snijgroep,
          entry: optimisticEntry,
          allowMixedParties: options.allowMixedParties === true,
        });
        pendingAddsRef.current.delete(optimisticEntry.id);

        if (!result?.ok) {
          updateQueues((currentQueues) => ({
            ...currentQueues,
            [snijgroep]: (currentQueues[snijgroep] || []).filter(
              (candidate) =>
                String(candidate.id) !== String(optimisticEntry.id)
            ),
          }));
          return result;
        }

        if (result.entry) {
          updateQueues((currentQueues) => ({
            ...currentQueues,
            [snijgroep]: (currentQueues[snijgroep] || []).map(
              (candidate) =>
                String(candidate.id) === String(optimisticEntry.id)
                  ? result.entry
                  : candidate
            ),
          }));
        }
        return result;
      } catch (error) {
        pendingAddsRef.current.delete(optimisticEntry.id);
        updateQueues((currentQueues) => ({
          ...currentQueues,
          [snijgroep]: (currentQueues[snijgroep] || []).filter(
            (candidate) =>
              String(candidate.id) !== String(optimisticEntry.id)
          ),
        }));
        throw error;
      }
    },
    [updateQueues]
  );

  const removeEntry = useCallback(async (snijgroep, id) => {
    if (!SNIJ_GROEPEN.includes(snijgroep)) {
      return { ok: false, reason: "invalid-group" };
    }
    const currentGroup = queuesRef.current[snijgroep] || [];
    const removedIndex = currentGroup.findIndex(
      (candidate) => String(candidate.id) === String(id)
    );
    const removedEntry =
      removedIndex >= 0 ? currentGroup[removedIndex] : null;
    updateQueues((currentQueues) => ({
      ...currentQueues,
      [snijgroep]: (currentQueues[snijgroep] || []).filter(
        (candidate) => String(candidate.id) !== String(id)
      ),
    }));

    const restoreRemovedEntry = () => {
      if (!removedEntry) return;
      updateQueues((latestQueues) => {
        const latestGroup = latestQueues[snijgroep] || [];
        if (
          latestGroup.some(
            (candidate) => String(candidate.id) === String(id)
          )
        ) {
          return latestQueues;
        }
        const restoredGroup = [...latestGroup];
        restoredGroup.splice(
          Math.min(removedIndex, restoredGroup.length),
          0,
          removedEntry
        );
        return {...latestQueues, [snijgroep]: restoredGroup};
      });
    };

    try {
      const result = await mutateSecurePlanning({
        operation: "remove",
        snijgroep,
        id,
      });
      if (!result?.ok) restoreRemovedEntry();
      return result;
    } catch (error) {
      restoreRemovedEntry();
      throw error;
    }
  }, [updateQueues]);

  const clearGroup = useCallback(async (snijgroep) => {
    if (!SNIJ_GROEPEN.includes(snijgroep)) {
      return { ok: false, reason: "invalid-group" };
    }
    const [planningResult, progressResult] = await Promise.all([
      mutateSecurePlanning({ operation: "clear", snijgroep }),
      mutateSecurePlanningProgress({ operation: "clear", snijgroep }),
    ]);
    if (planningResult?.ok) {
      updateQueues((currentQueues) => ({
        ...currentQueues,
        [snijgroep]: [],
      }));
    }
    if (progressResult?.completedEntryIdsByGroup) {
      setCompletedEntryIdsByGroup(
        progressResult.completedEntryIdsByGroup
      );
    }
    return planningResult;
  }, [updateQueues]);

  const getEntryIdFor = useCallback(
    (snijgroep, entry) => {
      if (!SNIJ_GROEPEN.includes(snijgroep) || !entry) return null;
      if (entry.id) return String(entry.id);
      const key = rowKeyFor(entry);
      const match = (queues[snijgroep] || []).find(
        (candidate) => rowKeyFor(candidate) === key
      );
      return match?.id ? String(match.id) : null;
    },
    [queues]
  );

  const isEntryCompleted = useCallback(
    (snijgroep, entry) => {
      const entryId = getEntryIdFor(snijgroep, entry);
      return !!entryId &&
        (completedEntryIdsByGroup[snijgroep] || []).includes(entryId);
    },
    [completedEntryIdsByGroup, getEntryIdFor]
  );

  const getOpenListFor = useCallback(
    (snijgroep) => {
      const completed = new Set(
        completedEntryIdsByGroup[snijgroep] || []
      );
      return (queues[snijgroep] || []).filter(
        (entry) => !completed.has(String(entry.id || ""))
      );
    },
    [completedEntryIdsByGroup, queues]
  );

  const markEntryCompleted = useCallback(
    async (snijgroep, entry) => {
      if (!SNIJ_GROEPEN.includes(snijgroep)) return;
      const entryId = getEntryIdFor(snijgroep, entry);
      if (!entryId ||
          (completedEntryIdsByGroup[snijgroep] || []).includes(entryId)) {
        return;
      }
      const result = await mutateSecurePlanningProgress({
        operation: "mark",
        snijgroep,
        entryId,
      });
      if (result?.completedEntryIdsByGroup) {
        setCompletedEntryIdsByGroup(
          result.completedEntryIdsByGroup
        );
      }
    },
    [completedEntryIdsByGroup, getEntryIdFor]
  );

  const getFirstRijFor = useCallback(
    (snijgroep) => getOpenListFor(snijgroep)[0] || null,
    [getOpenListFor]
  );

  const getNextRijFor = useCallback(
    (snijgroep, currentKey) => {
      const list = getOpenListFor(snijgroep);
      if (!list.length) return null;
      if (!currentKey) return list[0];
      const index = list.findIndex(
        (entry) => rowKeyFor(entry) === String(currentKey)
      );
      return index >= 0 && index < list.length - 1
        ? list[index + 1]
        : null;
    },
    [getOpenListFor]
  );

  return {
    queues,
    loading,
    completedEntryIdsByGroup,
    addEntry,
    removeEntry,
    clearGroup,
    getFirstRijFor,
    getNextRijFor,
    isEntryCompleted,
    markEntryCompleted,
  };
}
